import { useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import type { Matcher } from 'react-day-picker';
import { cn } from '@noor/lib/cn';
import { Button } from '../button';
import { Calendar } from '../calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { useFormFieldContext } from './form-field-context';
import { useFieldAria } from './use-field-aria';

const dateLabelFormatter = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' });

/**
 * Single-date picker. The RHF field value is a `Date | undefined`. Calendar +
 * popover are presentational; RHF owns the value.
 */
export function FormFieldDatePicker({
  placeholder = 'Pick a date',
  className,
  disabled = false,
  hideIcon = false,
  formatDate,
  disabledDates,
}: {
  placeholder?: string;
  className?: string;
  /** Disables the trigger (e.g. an open-ended promo has no dates). */
  disabled?: boolean;
  /** Drop the leading calendar glyph when the design shows text only. */
  hideIcon?: boolean;
  /** Override the trigger's date formatting (defaults to a medium en-US date). */
  formatDate?: (date: Date) => string;
  /**
   * Days the calendar refuses to select — a `react-day-picker` matcher, e.g.
   * `{ before: startOfToday }` for a window that may not start in the past.
   * Greying the days out is what makes the rule discoverable; validating on
   * submit alone lets someone pick a date and only then be told no.
   */
  disabledDates?: Matcher | Matcher[];
}) {
  const [open, setOpen] = useState(false);
  const { field } = useFormFieldContext();
  const aria = useFieldAria();
  const selected = field.value instanceof Date ? field.value : undefined;
  const format = formatDate ?? ((date: Date) => dateLabelFormatter.format(date));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          id={aria.id}
          aria-describedby={aria['aria-describedby']}
          aria-invalid={aria['aria-invalid']}
          className={cn(
            'h-10 w-full justify-start overflow-hidden rounded-full border border-input-border bg-transparent px-4 text-left text-sm font-normal tracking-[-0.28px] text-input-fg shadow-field',
            !selected && 'text-input-placeholder',
            className,
          )}
        >
          <span className="min-w-0 flex-1 truncate">
            {selected ? format(selected) : placeholder}
          </span>
          {hideIcon ? null : <CalendarIcon className="size-5 shrink-0" />}
        </Button>
      </PopoverTrigger>
      {/* `portal={false}` is load-bearing: most pickers live inside a Dialog
       * (the promotion modal, the venue-hours form), whose scroll-lock and
       * focus guards treat a body-portaled subtree as "outside" — the calendar
       * paints but its day buttons never receive the click. Rendering in place
       * keeps it within the dialog. `TimePicker` does the same. */}
      {/* `collisionPadding` alone is not enough. When the trigger sits low in
       * the viewport Radix FLIPS the calendar above it, and the calendar is
       * taller than the gap — so the top of it (the month caption and BOTH nav
       * arrows, which are absolutely positioned at the root's top) ends up at a
       * negative `y`, off the top edge. The day grid still shows, so the
       * calendar looks fine and merely reads as "stuck on the current month":
       * the arrows are painted and enabled but no pointer can reach them.
       *
       * `max-h` + `overflow-y-auto` bound the content to the space Radix
       * actually measured (`--radix-popover-content-available-height`), so the
       * calendar scrolls internally instead of overflowing the viewport, and
       * the nav row stays on-screen on either side of the flip. */}
      <PopoverContent
        className="max-h-[var(--radix-popover-content-available-height)] w-auto overflow-y-auto p-0"
        align="start"
        portal={false}
        collisionPadding={12}
      >
        <Calendar
          mode="single"
          {...(selected ? { selected } : {})}
          onSelect={(date) => {
            field.onChange(date);
            setOpen(false);
          }}
          onDayBlur={field.onBlur}
          {...(disabledDates ? { disabled: disabledDates } : {})}
        />
      </PopoverContent>
    </Popover>
  );
}
